import { Capacitor } from "@capacitor/core";

// ---------------------------------------------------------------------------
// Type definitions for the native plugin responses
// ---------------------------------------------------------------------------

interface HKWeightSample {
  date: string;
  kg: number;
}

interface HKCalorieSample {
  date: string;
  kcal: number;
}

interface HKWeightSamplesResult {
  samples: HKWeightSample[];
  available?: boolean;
}

interface HKCalorieSamplesResult {
  samples: HKCalorieSample[];
  available?: boolean;
}

interface HKPermissionResult {
  granted: boolean;
  available?: boolean;
}

interface HKWriteResult {
  success: boolean;
  available?: boolean;
}

type HKAuthorizationSummary = "authorized" | "denied" | "notDetermined" | "restricted";

interface HKAuthorizationStatusResult {
  available?: boolean;
  overallStatus?: HKAuthorizationSummary;
  read?: Record<string, HKAuthorizationSummary>;
  write?: Record<string, HKAuthorizationSummary>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Thin wrapper around the native HealthKitPlugin Capacitor plugin.
 * On web (non-native platforms) every export is a silent no-op.
 */
export function useHealthKit() {
  const isNative = Capacitor.isNativePlatform();

  // ------------------------------------------------------------------
  // requestPermission
  // ------------------------------------------------------------------
  async function requestPermission(): Promise<{ granted: boolean }> {
    if (!isNative) return { granted: false };
    const result = await (Capacitor.Plugins["HealthKit"] as {
      requestPermission: () => Promise<HKPermissionResult>;
    }).requestPermission();
    return { granted: result.granted };
  }

  // ------------------------------------------------------------------
  // syncWeightsFromHealth
  // ------------------------------------------------------------------
  async function syncWeightsFromHealth(
    since: Date
  ): Promise<Array<{ date: string; kg: number }>> {
    if (!isNative) return [];
    const result = await (Capacitor.Plugins["HealthKit"] as {
      readWeightSamples: (opts: { startDate: string; endDate: string }) => Promise<HKWeightSamplesResult>;
    }).readWeightSamples({
      startDate: since.toISOString(),
      endDate: new Date().toISOString(),
    });
    return result.samples ?? [];
  }

  // ------------------------------------------------------------------
  // syncActiveCaloriesFromHealth
  // ------------------------------------------------------------------
  async function syncActiveCaloriesFromHealth(
    since: Date
  ): Promise<Record<string, number>> {
    if (!isNative) return {};
    const result = await (Capacitor.Plugins["HealthKit"] as {
      readActiveCalories: (opts: { startDate: string; endDate: string }) => Promise<HKCalorieSamplesResult>;
    }).readActiveCalories({
      startDate: since.toISOString(),
      endDate: new Date().toISOString(),
    });
    const map: Record<string, number> = {};
    for (const s of result.samples ?? []) {
      map[s.date] = s.kcal;
    }
    return map;
  }

  // ------------------------------------------------------------------
  // writeWeightToHealth
  // ------------------------------------------------------------------
  async function writeWeightToHealth(date: string, kg: number): Promise<void> {
    if (!isNative) return;
    try {
      await (Capacitor.Plugins["HealthKit"] as {
        writeWeight: (opts: { date: string; kg: number }) => Promise<HKWriteResult>;
      }).writeWeight({ date, kg });
    } catch {
      // Best-effort write-back — swallow errors silently
    }
  }

  // ------------------------------------------------------------------
  // getAuthorizationStatus
  // ------------------------------------------------------------------
  async function getAuthorizationStatus(): Promise<HKAuthorizationStatusResult> {
    if (!isNative) return { available: false, overallStatus: "restricted" };
    try {
      const result = await (Capacitor.Plugins["HealthKit"] as {
        getAuthorizationStatus: () => Promise<HKAuthorizationStatusResult>;
      }).getAuthorizationStatus();
      return result ?? { available: false, overallStatus: "restricted" };
    } catch {
      return { available: false, overallStatus: "restricted" };
    }
  }

  return {
    requestPermission,
    syncWeightsFromHealth,
    syncActiveCaloriesFromHealth,
    writeWeightToHealth,
    getAuthorizationStatus,
  };
}
